import React, { useContext } from 'react';
import { DeepMap, FieldError, UseFormMethods, useWatch } from 'react-hook-form';
import { ICaseCreation } from '../../Schemas/ICaseCreation';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { GetWebCase_case } from '../../GraphQL';
import Input from '@ssg/common/Components/Input';
import Box from '../../Components/Layout/Box';
import InputPhone from '@ssg/common/Components/InputPhone';
import Checkbox from '@ssg/common/Components/Checkbox';
import classNames from 'classnames';
import TextButton from '@ssg/common/Components/TextButton';
import EnvironmentVariableContext from '@ssg/common/EnvironmentVariableContext';

interface Props {
	caseData: GetWebCase_case | undefined;
	register: UseFormMethods['register'];
	errors: DeepMap<ICaseCreation, FieldError>;
	control: UseFormMethods['control'];
	trigger: UseFormMethods['trigger'];
	setValue: UseFormMethods['setValue'];
}

const AdviserBox: React.FC<Props> = ({ caseData, register, errors, control, trigger, setValue }): React.ReactElement => {
	const envVar = useContext(EnvironmentVariableContext);

	const [showAdviser, setShowAdviser] = React.useState(typeof caseData !== 'undefined' && caseData.adviser !== null);

	const [showHelpText, setShowHelpText] = React.useState(false);
	const [country, setCountry] = React.useState(envVar.defaultCountry ?? '');
	const [companyCountry, setCompanyCountry] = React.useState(envVar.defaultCountry ?? '');

	const phonenumberHandler = React.useCallback(
		(phonenumber: string): void => {
			phonenumber = formatPhoneNumberIntl(phonenumber).replaceAll(/(\s|-)+/g, '');
			setValue('AdviserBox.phone', phonenumber, { shouldValidate: true });
		},
		[setValue],
	);

	const companyPhonenumberHandler = React.useCallback(
		(phonenumber: string): void => {
			phonenumber = formatPhoneNumberIntl(phonenumber).replaceAll(/(\s|-)+/g, '');
			setValue('AdviserBox.companyPhone', phonenumber, {
				shouldValidate: true,
			});
		},
		[setValue],
	);

	React.useEffect(() => {
		phonenumberHandler(caseData?.adviser?.phone ?? '');
	}, [caseData?.adviser, phonenumberHandler]);

	React.useEffect(() => {
		companyPhonenumberHandler(caseData?.adviser?.companyPhone ?? '');
	}, [caseData?.adviser, companyPhonenumberHandler]);

	const noEmail = useWatch({
		control,
		name: 'AdviserBox.noEmail',
		defaultValue: typeof caseData !== 'undefined' && caseData.adviser?.email === '',
	});

	React.useEffect(() => {
		noEmail && setValue('AdviserBox.email', '', { shouldValidate: true });
	}, [noEmail, setValue]);

	return (
		<Box form title="case.adviser" onClick={() => setShowHelpText(!showHelpText)} showHelpText={showHelpText} noPadding>
			<hr />
			<div className="w-full lg:w-3/4">
				<TextButton text={!showAdviser ? 'case.showAdviser' : 'case.hideAdviser'} icon={!showAdviser ? faChevronDown : faChevronUp} onClick={() => setShowAdviser(!showAdviser)} />

				<div
					className={classNames({
						hidden: !showAdviser,
					})}
				>
					<Input
						title="common.name"
						name="AdviserBox.name"
						innerRef={register}
						helpText="case.helpText.adviserName"
						showHelpText={showHelpText}
						errorMessage={errors.AdviserBox?.name?.message ?? ''}
						defaultValue={caseData?.adviser?.name ?? ''}
					/>

					<InputPhone
						title="common.phone"
						innerRef={register}
						name="AdviserBox.phone"
						errorMessage={errors.AdviserBox?.phone?.message ?? ''}
						helpText="case.helpText.adviserPhone"
						showHelpText={showHelpText}
						control={control}
						trigger={trigger}
						searchFn={(phonenumber): void => phonenumberHandler(phonenumber)}
						onCountryChange={(countrycode): void => setCountry(countrycode ?? '')}
						defaultCountry={country}
						defaultPhoneNumber={formatPhoneNumberIntl(caseData?.adviser?.phone ?? '').replaceAll(/(\s|-)+/g, '')}
					/>

					<Input
						title="common.email"
						name="AdviserBox.email"
						innerRef={register}
						helpText="case.helpText.adviserEmail"
						disabled={noEmail}
						showHelpText={showHelpText}
						errorMessage={errors.AdviserBox?.email?.message ?? ''}
						defaultValue={caseData?.adviser?.email}
					/>

					<Checkbox
						title="case.noEmail"
						name="AdviserBox.noEmail"
						className="mt-1"
						innerRef={register}
						onChange={() => {
							trigger('AdviserBox.email');
						}}
						defaultChecked={typeof caseData !== 'undefined' && caseData.adviser?.email === ''}
					/>

					<Input
						title="common.companyName"
						name="AdviserBox.companyName"
						innerRef={register}
						helpText="case.helpText.adviserCompanyName"
						showHelpText={showHelpText}
						errorMessage={errors.AdviserBox?.companyName?.message ?? ''}
						defaultValue={caseData?.adviser?.companyName ?? ''}
					/>

					<InputPhone
						title="common.companyPhone"
						innerRef={register}
						name="AdviserBox.companyPhone"
						errorMessage={errors.AdviserBox?.companyPhone?.message ?? ''}
						helpText="case.helpText.adviserCompanyPhone"
						showHelpText={showHelpText}
						control={control}
						trigger={trigger}
						searchFn={(phonenumber): void => companyPhonenumberHandler(phonenumber)}
						onCountryChange={(countrycode): void => setCompanyCountry(countrycode ?? '')}
						defaultCountry={companyCountry}
						defaultPhoneNumber={formatPhoneNumberIntl(caseData?.adviser?.companyPhone ?? '').replaceAll(/(\s|-)+/g, '')}
					/>
				</div>
			</div>
		</Box>
	);
};

export default AdviserBox;
